import type { Router } from 'vue-router'
import { useUserStore } from '@/store/modules/user'
import { useSeoMeta } from '@/utils/seo'
import { isBrowser } from '@/utils/ssr'

let NProgress: any = null
if (isBrowser) {
  import('nprogress').then((m) => {
    NProgress = m.default
    NProgress.configure({ showSpinner: false })
  })
  import('nprogress/nprogress.css')
}

const pageTitle : Record<string, string> = {
  '/': '星际高球联盟(星际高尔夫-Gibbous Golf) - 一站式大型高尔夫球场预订与高尔夫旅游定制平台 | 全球2000+球场实时预定',
  '/home': '星际高球联盟(星际高尔夫-Gibbous Golf) - 一站式大型高尔夫球场预订与高尔夫旅游定制平台 | 全球2000+球场实时预定',
}

// 各页面默认 SEO 描述
const pageDescriptions: Record<string, string> = {
  '/': '星际高球联盟是行业领先的高尔夫生活服务平台，已为 47 万高净值球友提供全球球场预订与高端旅游定制服务。我们致力于以卓越的资源整合与贴心的一站式服务，为您连接全球 2000+ 球场，打造专属您的品质高尔夫生活圈层。',
  '/home': '星际高球联盟是行业领先的高尔夫生活服务平台，已为 47 万高净值球友提供全球球场预订与高端旅游定制服务。我们致力于以卓越的资源整合与贴心的一站式服务，为您连接全球 2000+ 球场，打造专属您的品质高尔夫生活圈层。',
  '/course/list': '浏览全国高尔夫球场，按地区筛选球场，查看球场价格和开球时间。支持国内外球场搜索。',
  '/setmeal/list': '精选高尔夫套餐，球场+酒店组合优惠，超值高尔夫旅行套餐，一站式预订。',
  '/scenic/list': '探索全国高尔夫旅行精选景点，查看景点介绍、开放时间和人均消费，打造专属高尔夫旅行体验。',
  '/restaurant/list': '精选高尔夫球场周边优质餐厅，查看餐厅简介、人均消费和营业时间，高尔夫旅行美食一站搞定。',
  '/hotel/list': '精选高尔夫旅行酒店，查看酒店星级、设施与房价，一站式预订高尔夫旅行住宿。',
  '/login': '登录星际高球联盟，享受高尔夫球场在线预订服务。',
  '/booking/confirm': '确认高尔夫球场预订订单，选择球友、查看费用明细。',
  '/booking/setmeal-confirm': '确认高尔夫套餐预订订单，查看套餐详情与费用。',
  '/payment': '安全支付，支持微信支付、支付宝等多种支付方式。',
  '/payment/result': '支付结果确认，查看订单支付状态。',
  '/order/list': '查看我的高尔夫球场预订订单，管理订单状态。',
  '/user/profile': '个人中心，管理账户信息、VIP等级和预订记录。',
  '/user/edit': '编辑个人资料，修改头像、昵称等个人信息。',
}

// 各页面独立关键词（详情页由组件自行设置）
const pageKeywords: Record<string, string> = {
  '/': '星际高球联盟,高尔夫球场预订,高尔夫旅游定制,高尔夫,星际高尔夫,全球高尔夫TeeTime,高尔夫记分APP,高尔夫社区,商务高尔夫,高净值运动消费,高尔夫球友社交,高端高尔夫度假,高尔夫服务平台',
  '/home': '星际高球联盟,高尔夫球场预订,高尔夫旅游定制,高尔夫,星际高尔夫,全球高尔夫TeeTime,高尔夫记分APP,高尔夫社区,商务高尔夫,高净值运动消费,高尔夫球友社交,高端高尔夫度假,高尔夫服务平台',
  '/course/list': '高尔夫球场,球场列表,球场筛选,国内球场,海外球场,球场价格,开球时间查询',
  '/setmeal/list': '高尔夫套餐,球场套餐,高尔夫旅游,球场酒店套餐,高尔夫优惠',
  '/scenic/list': '景点游览,高尔夫旅行,打卡景点,景点推荐,旅游景点,星际高球联盟',
  '/restaurant/list': '高尔夫餐厅,球场美食,高尔夫旅行餐厅,餐厅推荐,星际高球联盟',
  '/hotel/list': '高尔夫酒店,旅行住宿,酒店预订,星级酒店,高尔夫旅行,星际高球联盟',
}

export function setupGuards(router: Router) {
  router.beforeEach((to, _from) => {
    NProgress?.start()

    // 设置页面 SEO（详情页由组件自行设置更精确的 meta，跳过守卫的默认设置）
    const isDetailPage = to.path.startsWith('/course/detail/')
      || to.path.startsWith('/setmeal/detail/')
      || to.path.startsWith('/scenic/detail/')
      || to.path.startsWith('/hotel/detail/')
      || to.path.startsWith('/restaurant/detail/')
    if (!isDetailPage) {
      const title = pageTitle[to.path] || to.meta.title as string
      const desc = pageDescriptions[to.path] || '星际高球联盟 - 专业高尔夫球场预订平台'
      const keywords = pageKeywords[to.path] || '高尔夫,球场预订,开球时间,高尔夫套餐,Golf,tee time,星际高球'
      useSeoMeta({
        title,
        description: desc,
        keywords,
        ogUrl: `https://www.gibbous.net${to.fullPath}`,
      })
    }

    const userStore = useUserStore()
    const isLoggedIn = userStore.isLoggedIn

    if (to.meta.requiresAuth && !isLoggedIn) {
      return { path: '/login', query: { redirect: to.fullPath } }
    } else if (to.path === '/login' && isLoggedIn) {
      return { path: '/home' }
    } else if (_from.path === '/login' && !isLoggedIn && to.path !== '/login') {
      return false
    }
  })

  router.afterEach(() => {
    NProgress?.done()
  })
}
